import { ctaFeatures, gisFeatures, carbonFeatures } from "@/content/coletor-publico-landing";

const CTA_IMAGE =
  "https://readdy.ai/api/search-image?query=Close%20up%20of%20large%20mature%20tree%20trunk%20with%20botanist%20measuring%20tape%2C%20urban%20park%20setting%2C%20warm%20sunlight%20filtering%20through%20leaves%2C%20professional%20environmental%20survey%2C%20editorial%20photography%20with%20soft%20bokeh&width=800&height=600&seq=floraiq-feat-cta-02&orientation=landscape";
const GIS_IMAGE =
  "https://readdy.ai/api/search-image?query=Aerial%20satellite%20view%20of%20green%20urban%20neighborhood%20with%20streets%20and%20parks%2C%20top-down%20perspective%2C%20lush%20tree%20canopy%2C%20clean%20modern%20mapping%20aesthetic%2C%20bright%20daylight%2C%20editorial%20photography&width=800&height=600&seq=floraiq-feat-gis-02&orientation=landscape";
const CARBON_IMAGE =
  "https://readdy.ai/api/search-image?query=Dense%20tropical%20forest%20canopy%20from%20above%20with%20sunlight%20breaking%20through%2C%20rich%20green%20layers%2C%20carbon%20sequestration%20concept%2C%20clean%20nature%20photography%2C%20vibrant%20greens%2C%20editorial%20style&width=800&height=600&seq=floraiq-feat-carbon-02&orientation=landscape";

function FeatureBlock({
  id,
  label,
  title,
  subtitle,
  features,
  accent,
  imageUrl,
  reverse,
}: {
  id: string;
  label: string;
  title: string;
  subtitle: string;
  features: string[];
  accent: "primary" | "accent";
  imageUrl: string;
  reverse?: boolean;
}) {
  const accentBg = accent === "primary" ? "bg-primary-50" : "bg-accent-50";
  const accentText = accent === "primary" ? "text-primary-600" : "text-accent-600";
  const accentBorder = accent === "primary" ? "border-primary-200" : "border-accent-200";
  const accentLabel = accent === "primary" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700";

  return (
    <div id={id} className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className={`flex flex-col ${reverse ? "lg:flex-row-reverse" : "lg:flex-row"} gap-10 md:gap-14 lg:gap-20 items-center`}>
          {/* Text */}
          <div className="flex-1 w-full">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${accentLabel} mb-4`}>
              {label}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {title}
            </h2>
            <p className="text-base md:text-lg text-foreground-600 mb-7 md:mb-10 leading-relaxed max-w-xl">
              {subtitle}
            </p>
            <ul className="space-y-3.5">
              {features.map((feat) => (
                <li key={feat} className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full ${accentBg} flex items-center justify-center shrink-0 mt-0.5`}>
                    <i className={`ri-check-line ${accentText} text-xs w-full h-full flex items-center justify-center`} />
                  </div>
                  <span className="text-sm md:text-[15px] text-foreground-700">{feat}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Visual with image background */}
          <div className="flex-1 w-full">
            {id === "cta-arboreo" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
                <div className="relative p-5 md:p-7">
                  <div className={`bg-white/95 backdrop-blur-sm rounded-xl border ${accentBorder} p-4 md:p-5`}>
                    <div className="text-[11px] text-foreground-500 uppercase tracking-wider mb-3">Ficha Cadastral Arbórea</div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {["Código CTA", "Espécie", "Localização", "DAP e altura", "Fitossanidade", "Classe de risco", "Recomendação", "Fotos e evidências"].map((label) => (
                        <div key={label} className="bg-background-50 rounded-lg p-2.5 text-sm font-medium text-foreground-800">
                          {label}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {id === "ambiental" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm aspect-[4/3]">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/60" />
                <div className="relative h-full p-5 md:p-7 flex flex-col justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                      <i className="ri-map-pin-line text-white text-sm w-full h-full flex items-center justify-center" />
                    </div>
                    <span className="text-sm font-semibold text-white">Mapa Arbóreo — Goiânia</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {["Risco", "Espécie", "Bairro", "Projeto"].map((f) => (
                      <span key={f} className="px-2.5 py-1 bg-white/20 backdrop-blur rounded-md text-xs text-white border border-white/20">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {id === "carbono" && (
              <div className="relative rounded-2xl overflow-hidden border border-background-200/70 shadow-sm">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${imageUrl})` }} />
                <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent" />
                <div className="relative p-5 md:p-7">
                  <div className="text-sm font-semibold text-white mb-5">Indicadores de Carbono</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {["Biomassa arbórea", "Carbono e CO₂e estimados", "Áreas verdes mapeadas", "Replantios", "Compensações ambientais", "Projetos monitorados"].map((label) => (
                      <li key={label} className="bg-white/15 backdrop-blur rounded-lg px-3 py-2.5 text-sm text-white border border-white/10">
                        {label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  return (
    <div>
      <FeatureBlock
        id="cta-arboreo"
        label="Inventário Arbóreo"
        title="Do inventário à gestão permanente da arborização urbana"
        subtitle="O FloraIQ permite cadastrar, monitorar e gerenciar cada árvore da cidade com dados técnicos, georreferenciamento e histórico completo."
        features={ctaFeatures}
        accent="primary"
        imageUrl={CTA_IMAGE}
      />

      <div className="bg-background-100">
        <FeatureBlock
          id="ambiental"
          label="GIS Ambiental"
          title="Visualize o patrimônio verde no território"
          subtitle="Mapa interativo com camadas temáticas, filtros inteligentes e exportação GeoJSON para integração com sistemas GIS municipais."
          features={gisFeatures}
          accent="accent"
          imageUrl={GIS_IMAGE}
          reverse
        />
      </div>

      <FeatureBlock
        id="carbono"
        label="Carbono Urbano"
        title="Dados ambientais que apoiam sustentabilidade, ESG e planejamento urbano"
        subtitle="Estimativas de carbono sequestrado, biomassa arbórea, indicadores de áreas verdes e monitoramento de compensações ambientais."
        features={carbonFeatures}
        accent="primary"
        imageUrl={CARBON_IMAGE}
      />
    </div>
  );
}